import { Button, Card, CardActions, CardContent, Chip, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { EmptyState, ErrorMessage } from 'components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Lesson, Mastery, Module } from "services";
import useErrorCatcher from "utils/useErrorCatcher";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  label: {
    fontSize: 18
  },
  section: {
    '& + &': {
      marginTop: theme.spacing(5)
    }
  },
  chip: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}));

let mounted = false;

const ModuleSet = props => {
  const { open, onClose, className, reload, ...rest } = props;

  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));
  const model = useSelector(state => state.models.lesson);
  const [modules, setModules] = useState(model.modules);
  const [masteries, setMasteries] = useState([]);
  const [options, setOptions] = useState([]);
  const [mastery, setMastery] = useState("");

  useEffect(() => {
    mounted = true;
    runAsyncBlock(async () => {
      const { models } = await Mastery.list({ query: { limit: 9999 } });
      mounted && setMasteries(models);
      return () => { mounted = false };
    }).finally(() => mounted && setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (mounted) {
      setModules(model.modules);
      setErrorMessage("");
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    runAsyncBlock(async () => {
      const { models } = await Module.list({ query: { limit: 9999, mastery_level: mastery.id } });
      mounted && setOptions(models);
    }).finally(() => mounted && setLoading(false));
  }, [mastery]); // eslint-disable-line

  if (!open) {
    return null;
  }

  const handleClose = event => {
    event.preventDefault();

    mounted && setLoading(true);
    runAsyncBlock(async () => {
      await Lesson.set_modules(model.id, { modules: modules.map(mmodule => mmodule.id) });
      mounted && setLoading(false);
      reload();
    }).finally(() => {
      onClose();
    });

  }
  const handleMasteryChange = event => {
    event.persist();
    let id = event.target.value;
    let mastery = masteries.find(mastery => mastery.id === parseInt(id))
    setMastery(mastery.id);
  };

  const handleChange = event => {
    event.persist();
    let id = event.target.value
    if (!modules.find(mmodule => mmodule.id === id)) {
      let mmodule = options.find(mmodule => mmodule.id === parseInt(id))
      if (mmodule) {
        mounted && setModules([...modules, mmodule]);
      }
    }
  };

  const deleteModule = (id) => {
    let newModules = [...modules];
    let index = newModules.findIndex(mmodule => mmodule.id === id);
    if (index >= 0) {
      newModules.splice(index, 1);
      mounted && setModules(newModules);
    }
  }

  return (
    <Modal
      onClose={onClose}
      open={open}>
      <Card
        {...rest}
        className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Set Assigned Modules
            </Typography>
            <Typography gutterBottom variant="body2" color="error">This function has been deprecated. Any changes done here might not reflect as intended.</Typography>
            <div className={classes.section}>
              <Card>
                <CardContent className={classes.content}>
                  {modules.map(mmodule => (
                    <Chip
                      key={mmodule.id}
                      className={classes.chip}
                      label={mmodule.name_en}
                      onDelete={() => deleteModule(mmodule.id)}
                    />
                  ))}
                  <EmptyState active={!modules.length} message="No modules assigned." />
                </CardContent>
              </Card>
            </div>
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={12} xs={12}>
                <TextField
                  fullWidth
                  label="Mastery"
                  name="mastery"
                  onChange={handleMasteryChange}
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{ native: true }}
                  value={mastery}
                  variant="outlined"
                  InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
                  select
                >
                  <option disabled value={""} label="Select Mastery" />
                  {masteries && masteries.length > 0 && masteries.map((mastery, index) => (
                    <option key={index} value={mastery.id} >{mastery.name_en}{mastery.name_zh}</option>
                  ))}
                </TextField>
              </Grid>
              <Grid item md={12} xs={12}>
                <TextField
                  fullWidth
                  label="Add Module"
                  name="selected"
                  onChange={handleChange}
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{ native: true }}
                  value={""}
                  variant="outlined"
                  InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
                  select
                  disabled={!mastery}
                >
                  <option disabled value={""} label="Select Module" />
                  {options && options.length > 0 && options.map((opt, index) => (
                    <option key={index} value={opt.id} disabled={modules.find(mmodule => mmodule.id === opt.id)} >{opt.name_en}</option>
                  ))}
                </TextField>
              </Grid>
              <ErrorMessage message={errorMessage} className={classes.error} />
              <Grid item />
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleClose} variant="contained" disabled={loading} >Save</Button>
          </CardActions>
        </form>
      </Card>
    </Modal >
  );
};

ModuleSet.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool
};

ModuleSet.defaultProps = {
  open: false,
  onClose: () => { }
};

export default ModuleSet;
